import React, { useContext } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Skeleton, SkeletonItem } from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useResponsiveStyles } from '@bingads-webui-campaign-react/common-hooks';
import { ValidatedBudgetInput } from '@bingads-webui-campaign-react/budget-component-v2';
import { HelpIcon } from '@bingads-webui-campaign-react/help';
import { biddingStrategyTypeId } from '@bingads-webui-campaign-react/constants';
import {
  BudgetTierTitles,
  BudgetDetailKeysByBidType,
  BudgetDetailsKeysText,
  BudgetDetailKeysByCampaignType,
} from '../model/constants';

const getRadioStyles = () => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  radioTitle: {
    fontSize: '16px',
    fontWeight: '600',
  },
  flexBox: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  leftBox: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '5px',
  },
  budgetDay: {
    fontSize: '14px',
    fontWeight: '600',
    borderRight: '1px solid rgba(224, 224, 224, 1)',
    verticalAlign: 'middle',
    paddingRight: '5px',
    height: 20,
    width: 'auto',
    display: 'inline-block',
  },
  budgetMonth: {
    fontSize: '14px',
    fontWeight: '600',
    height: 20,
    width: 'auto',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
  },
  metric: {
    display: 'flex',
    flexDirection: 'column',
  },
  rightBox: {
    display: 'grid',
    'grid-template-columns': 'repeat(2, 1fr)',
    width: '250px',
    columnGap: '10px',
  },
  badge: {
    borderRadius: '999px',
    textAlign: 'center',
    width: 81,
    height: 20,
    border: '1px solid rgba(159, 216, 159, 1)',
    color: 'rgba(14, 112, 14, 1)',
    fontSize: '10px',
    fontWeight: '600',
    background: 'rgba(241, 250, 241, 1)',
  },
  metricTitle: {
    fontSize: '10px',
    fontWeight: '400',
    color: 'rgba(97, 97, 97, 1)',
  },
  metricValue: {
    fontWeight: '600',
    fontSize: '14px',
  },
  budgetDisclaimer: {
    width: 'auto',
    fontSize: '10px',
    color: 'rgba(112, 112, 112, 1)',
    marginTop: '10px',
  },
  skeletionStyle: {
    height: 20,
    width: 60,
    display: 'inline-block',
    marginRight: '5px',
  },
});

export const Metrics = ({
  budgetDetails,
  isLoading,
  bidType,
  campaignType,
}) => {
  const i18n = useContext(I18nContext);

  const getStyles = useResponsiveStyles(getRadioStyles);

  const budgetDetailsKeys = BudgetDetailKeysByBidType[biddingStrategyTypeId[bidType]] || BudgetDetailKeysByCampaignType[campaignType] || BudgetDetailKeysByBidType.Default;

  return budgetDetailsKeys.map(key => (
    <div key={key} className={classNames(getStyles('column'))}>
      <div className={classNames(getStyles('metric'))}>
        <span className={classNames(getStyles('metricTitle'))}>
          {
            isLoading ?
              <Skeleton style={{ marginTop: '5px', marginBottom: '5px', width: '65px' }} appearance="translucent" animation="wave">
                <SkeletonItem />
              </Skeleton>
              :
              <>
                {i18n.getString(BudgetDetailsKeysText[key].title)}&nbsp;
                <HelpIcon topic={BudgetDetailsKeysText[key].popupId} />
              </>
          }
        </span>
        <span className={classNames(getStyles('metricValue'))}>
          {
            isLoading ?
              <Skeleton style={{ width: '95px' }} width="100" appearance="translucent" animation="wave">
                <SkeletonItem />
              </Skeleton>
              : budgetDetails && budgetDetails[key].Range
          }
        </span>
      </div>
    </div>
  ));
};

export const BudgetItemLabel = ({
  tier,
  data,
  isLoading,
  currency,
  campaignType,
  bidType,
}) => {
  const getStyles = useResponsiveStyles(getRadioStyles);

  const i18n = useContext(I18nContext);

  const {
    BudgetAmountDaily,
    BudgetAmountMonthly,
    BudgetDetails,
  } = data || {};

  return (
    <div className={classNames(getStyles('root'), `budget-item-${tier}`)}>
      <div className={classNames(getStyles('flexBox'))}>
        <div className={classNames(getStyles('leftBox'))}>
          <span className={classNames(getStyles('radioTitle'))}>
            {i18n.getString(BudgetTierTitles[tier])}
          </span>
          <div>
            <span className={classNames(getStyles('budgetDay'))}>
              {isLoading ?
                <Skeleton className={classNames(getStyles('skeletionStyle'))} appearance="translucent" animation="wave" as="span">
                  <SkeletonItem className={classNames(getStyles('skeletionStyle'))} as="span" size={16} />
                </Skeleton>
                : `${i18n.formatCurrency(BudgetAmountDaily, currency)} /${i18n.getString(_TL_('day'))}`
              }
            </span>
            &nbsp;
            <span className={classNames(getStyles('budgetMonth'))}>
              {
                isLoading ?
                  <Skeleton className={classNames(getStyles('skeletionStyle'))} appearance="translucent" animation="wave" as="span">
                    <SkeletonItem className={classNames(getStyles('skeletionStyle'))} as="span" size={16} />
                  </Skeleton>
                  : `${i18n.formatCurrency(BudgetAmountMonthly, currency)}/${i18n.getString(_TL_('month'))}`
              }
            </span>
          </div>
        </div>
        <div className={classNames(getStyles('rightBox'))}>
          <Metrics budgetDetails={BudgetDetails} isLoading={isLoading} bidType={bidType} campaignType={campaignType} />
        </div>
      </div>
    </div>
  );
};

BudgetItemLabel.propTypes = {
  tier: PropTypes.string.isRequired,
  data: PropTypes.shape({}).isRequired,
  isLoading: PropTypes.bool.isRequired,
  bidType: PropTypes.string.isRequired,
  campaignType: PropTypes.string.isRequired,
  currency: PropTypes.string,
};

BudgetItemLabel.defaultProps = {
  currency: 'USD',
};

export const CustomBudgetLabel = ({
  isSelected,
  currency,
  onUpdateCustomBudget,
  customPerformanceData,
  hidePerformanceData,
  customBudgetResult,
  bidType,
  defaultBudget,
  minBudget,
  campaignType,
  maxBudget,
  showDropdown,
  onSelectCurrency,
  currencies,
}) => {
  const getStyles = useResponsiveStyles(getRadioStyles);

  const i18n = useContext(I18nContext);

  const {
    BudgetDetails,
    isLoading,
  } = customPerformanceData || {};

  return (
    <div className={classNames(getStyles('root'), 'budget-item-CustomBudget')}>
      <div className={classNames(getStyles('flexBox'))}>
        <div className={classNames(getStyles('leftBox'))}>
          <span className={classNames(getStyles('radioTitle'))}> {i18n.getString(_TL_('Set your own budget'))} </span>
          {
            isSelected && (
              <>
                <ValidatedBudgetInput
                  validationProps={{
                    onValidate: onUpdateCustomBudget,
                    data: (customBudgetResult && customBudgetResult.data) || { budget: defaultBudget },
                    initialValue: defaultBudget,
                    alwaysShowError: (customBudgetResult && customBudgetResult.isValid === false),
                  }}
                  budgetKey="budget"
                  currency={currency || 'USD'}
                  label={i18n.getString(_TL_('Set the campaign\'s average daily budget'))}
                  className="custom-budget-input"
                  minBudget={minBudget}
                  maxBudget={maxBudget}
                  showDropdown={showDropdown}
                  onSelectCurrency={onSelectCurrency}
                  currencies={currencies}
                />
              </>
            )
          }
        </div>
        <div className={classNames(getStyles('rightBox'))}>
          {
            // show two metrics in a column
            !hidePerformanceData && isSelected &&
            <Metrics budgetDetails={BudgetDetails} isLoading={isLoading} bidType={bidType} campaignType={campaignType} />
          }
        </div>
      </div>
    </div>
  );
};


CustomBudgetLabel.propTypes = {
  isSelected: PropTypes.bool.isRequired,
  currency: PropTypes.string.isRequired,
  bidType: PropTypes.string.isRequired,
  onUpdateCustomBudget: PropTypes.func.isRequired,
  customPerformanceData: PropTypes.shape({
    BudgetDetails: PropTypes.shape({}),
    isLoading: PropTypes.bool,
  }),
  hidePerformanceData: PropTypes.bool,
  defaultBudget: PropTypes.number.isRequired,
  campaignType: PropTypes.string.isRequired,
  showDropdown: PropTypes.bool,
  onSelectCurrency: PropTypes.func,
  currencies: PropTypes.arrayOf(PropTypes.shape({})),
};

CustomBudgetLabel.defaultProps = {
  hidePerformanceData: false,
  customPerformanceData: {
    BudgetDetails: {},
    isLoading: false,
  },
  showDropdown: false,
  onSelectCurrency: _.noop,
  currencies: [],
};
